import { useMemo, useState } from "react";
import { useSubs } from "@/context/SubsContext";
import { formatEUR, toMonthly, frequencyLabel, FREQUENCIES } from "@/lib/subscriptions";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowDown, Trash2, Repeat, Equal } from "lucide-react";

// Simulation action per subscription: "keep", "cancel", "replace"
export default function Simulator() {
  const { subs, loading } = useSubs();
  const active = useMemo(() => subs.filter((s) => s.status === "actif"), [subs]);

  const [selected, setSelected] = useState({}); // { subId: true }
  const [actions, setActions] = useState({}); // { subId: { action, newPrice, newFrequency, fees } }

  const setAction = (subId, patch) => {
    setActions((a) => ({ ...a, [subId]: { ...(a[subId] || { action: "keep" }), ...patch } }));
  };

  const toggle = (subId) => {
    setSelected((prev) => {
      const next = { ...prev, [subId]: !prev[subId] };
      if (next[subId] && !actions[subId]) {
        setAction(subId, { action: "cancel", newPrice: "", newFrequency: "mensuelle", fees: "" });
      }
      return next;
    });
  };

  const sim = useMemo(() => {
    let currentMonthly = 0;
    let newMonthly = 0;
    let totalFees = 0;
    active.forEach((s) => {
      const cur = toMonthly(s.price, s.frequency);
      currentMonthly += cur;

      const act = selected[s.id] ? actions[s.id] : null;
      if (!act || act.action === "keep") {
        newMonthly += cur;
      } else if (act.action === "cancel") {
        // excluded
      } else if (act.action === "replace") {
        const price = parseFloat(String(act.newPrice).replace(",", "."));
        const freq = act.newFrequency || "mensuelle";
        if (Number.isFinite(price) && price > 0) {
          newMonthly += toMonthly(price, freq);
        } else {
          newMonthly += cur; // if no price, keep original
        }
        const fees = parseFloat(String(act.fees).replace(",", "."));
        if (Number.isFinite(fees) && fees > 0) totalFees += fees;
      }
    });
    const monthlySavings = currentMonthly - newMonthly;
    return {
      currentMonthly,
      newMonthly,
      monthlySavings,
      yearlySavings: monthlySavings * 12,
      totalFees,
    };
  }, [active, selected, actions]);

  if (loading) {
    return <div className="text-gray-500 py-10 text-center">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="simulator-root">
      {/* Summary */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="sim-current">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Dépenses actuelles</div>
          <div className="font-heading text-3xl font-bold text-gray-900 mt-2">{formatEUR(sim.currentMonthly)}</div>
          <div className="text-xs text-gray-500">/ mois</div>
        </div>
        <div className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="sim-new">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Nouvelles dépenses</div>
          <div className="font-heading text-3xl font-bold text-gray-900 mt-2">{formatEUR(sim.newMonthly)}</div>
          <div className="text-xs text-gray-500">/ mois</div>
        </div>
        <div className="rounded-2xl p-6 border shadow-soft bg-emerald-50 border-emerald-100" data-testid="sim-monthly-savings">
          <div className="text-xs text-emerald-700 uppercase tracking-wider font-bold">Économie mensuelle</div>
          <div className="font-heading text-3xl font-bold text-emerald-900 mt-2">
            {sim.monthlySavings >= 0 ? "+" : ""}{formatEUR(sim.monthlySavings)}
          </div>
          <div className="text-xs text-emerald-700">/ mois</div>
        </div>
        <div className="rounded-2xl bg-emerald-600 p-6 border border-emerald-700 text-white shadow-soft-lg" data-testid="sim-yearly-savings">
          <div className="text-xs text-emerald-100 uppercase tracking-wider font-bold">Économie annuelle</div>
          <div className="font-heading text-3xl font-bold mt-2">
            {sim.yearlySavings >= 0 ? "+" : ""}{formatEUR(sim.yearlySavings)}
          </div>
          <div className="text-xs text-emerald-100">/ an</div>
        </div>
      </section>

      {sim.totalFees > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          Frais ou conditions à prendre en compte : <strong>{formatEUR(sim.totalFees)}</strong>.
          Vérifiez les conditions de résiliation ou de changement auprès de votre fournisseur.
        </div>
      )}

      {/* Subs list */}
      <section className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft">
        <div className="mb-5">
          <h3 className="font-heading text-lg font-bold text-gray-900">Simuler vos abonnements</h3>
          <p className="text-sm text-gray-500">Sélectionnez un abonnement, puis choisissez une action pour voir l'impact instantanément.</p>
        </div>
        {active.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">Aucun abonnement actif.</div>
        ) : (
          <ul className="space-y-3">
            {active.map((s) => {
              const isSel = !!selected[s.id];
              const act = actions[s.id] || { action: "keep", newPrice: "", newFrequency: "mensuelle", fees: "" };
              return (
                <li
                  key={s.id}
                  data-testid={`sim-row-${s.id}`}
                  className={`rounded-xl border p-4 transition-colors ${isSel ? "border-emerald-300 bg-emerald-50/40" : "border-gray-100 bg-white"}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={() => toggle(s.id)}
                      data-testid={`sim-toggle-${s.id}`}
                      className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900">{s.name}</div>
                      <div className="text-xs text-gray-500">
                        {formatEUR(toMonthly(s.price, s.frequency))} /mois · {frequencyLabel(s.frequency)}
                      </div>
                    </div>
                    {isSel && (
                      <Select
                        value={act.action}
                        onValueChange={(v) => setAction(s.id, { action: v })}
                      >
                        <SelectTrigger data-testid={`sim-action-${s.id}`} className="w-[160px] rounded-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="keep"><Equal className="inline mr-2" size={14} />Conserver</SelectItem>
                          <SelectItem value="cancel"><Trash2 className="inline mr-2" size={14} />Résilier</SelectItem>
                          <SelectItem value="replace"><Repeat className="inline mr-2" size={14} />Remplacer</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {isSel && act.action === "replace" && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 pl-9">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Nouveau prix (€)"
                        value={act.newPrice}
                        onChange={(e) => setAction(s.id, { newPrice: e.target.value })}
                        data-testid={`sim-newprice-${s.id}`}
                        className="rounded-xl focus-visible:ring-emerald-500"
                      />
                      <Select
                        value={act.newFrequency}
                        onValueChange={(v) => setAction(s.id, { newFrequency: v })}
                      >
                        <SelectTrigger data-testid={`sim-newfreq-${s.id}`} className="rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCIES.map((f) => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Frais / préavis (€)"
                        value={act.fees}
                        onChange={(e) => setAction(s.id, { fees: e.target.value })}
                        data-testid={`sim-fees-${s.id}`}
                        className="rounded-xl focus-visible:ring-emerald-500"
                      />
                    </div>
                  )}
                  {isSel && act.action === "cancel" && (
                    <div className="mt-3 pl-9 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Frais de résiliation éventuels (€)"
                        value={act.fees}
                        onChange={(e) => setAction(s.id, { fees: e.target.value })}
                        data-testid={`sim-cancelfees-${s.id}`}
                        className="rounded-xl focus-visible:ring-emerald-500"
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex justify-end mt-6">
          <Button
            variant="ghost"
            onClick={() => { setSelected({}); setActions({}); }}
            data-testid="sim-reset"
            className="rounded-full text-gray-600"
          >
            Réinitialiser
          </Button>
        </div>
      </section>
    </div>
  );
}
